<template>
  <div class="row">
    <div class="col-md-12 col-lg-12">
      <div class="search-bar">
        <div class="col-sm-12 no-padding">
          <div class="pull-left">
            <el-input v-model="searchQuery" @input="onInput()" :placeholder="$t('reports.search')" class="icon-search"></el-input>
          </div>
          <div class="pull-right">
            <router-link class="btn add" :title="$t('general.add') + ' ' + $t('marketingSubscriptions.single')" :to="{ path: '/marketing/subscriptions/add' }">
              <img src="/static/img/icons/add.png">
              {{ $t('general.add') }}
            </router-link>
          </div>
        </div>
      </div>

      <div v-loading="loading" class="sub-families">
        <div v-if="!loading && !families.length" class="card">
          <div class="card-content text-center text-muted sub-empty">{{ $t('marketingSubscriptions.noSubscriptions') }}</div>
        </div>

        <!-- One box per family; its levels are the rows, lowest first -->
        <div v-for="family in families" :key="'family' + family.id" class="card sub-family">
          <div class="sub-family__head">
            <div class="sub-family__title">
              <i class="fa fa-clone" aria-hidden="true"></i>
              <strong>{{ family.name }}</strong>
              <span class="sub-family__meta">
                · {{ family.levels.length === 1 ? $t('marketingSubscriptions.oneLevel') : $t('marketingSubscriptions.levelsCount', { count: family.levels.length }) }}
                · {{ $t('marketingSubscriptions.activeSubscribersCount', { count: family.active_subscribers }) }}
                · {{ $t('marketingSubscriptions.paymentProcessor') }}: {{ family.payment_group_name ? paymentGroupLabel({ group_name: family.payment_group_name, gateway: family.payment_gateway }) : '-' }}
              </span>
            </div>
            <div class="sub-family__actions">
              <a class="btn btn-sm btn-default" @click="openSubscribers(family)">
                <i class="fa fa-users"></i> {{ $t('marketingSubscriptions.allSubscribers') }}
              </a>
              <a class="btn btn-sm btn-default" @click="openFamilyModal(family)">
                <i class="fa fa-pencil"></i> {{ $t('marketingSubscriptions.editFamily') }}
              </a>
            </div>
          </div>

          <div class="sub-grid sub-grid--head">
            <span></span>
            <span>{{ $t('marketingSubscriptions.name') }}</span>
            <span>{{ $t('marketingSubscriptions.price') }}</span>
            <span>{{ $t('marketingSubscriptions.benefits') }}</span>
            <span>{{ $t('marketingSubscriptions.subscribers') }}</span>
            <span>{{ $t('marketingSubscriptions.status') }}</span>
            <span></span>
          </div>

          <div v-for="level in family.levels" :key="'level' + level.id" class="sub-grid sub-row" :class="{ 'is-inactive': !level.active }">
            <div class="sub-level">{{ level.level }}</div>
            <div class="sub-name">
              <img v-if="level.image_url" :src="level.image_url" class="sub-thumb">
              <a class="sub-name__link" @click="editLevel(level)">{{ level.name }}</a>
              <span v-if="!level.active" class="sub-badge sub-badge--muted" :title="$t('marketingSubscriptions.inactiveHint')">{{ $t('marketingSubscriptions.inactive') }}</span>
            </div>
            <div>
              {{ level.price }} {{ currency }}
              <small v-if="level.price_diff !== null" class="text-muted">(+{{ level.price_diff }})</small>
            </div>
            <div>
              <el-tooltip v-if="level.inherited_coupons.length" placement="top">
                <div slot="content">
                  <div class="sub-tooltip-title">{{ $t('marketingSubscriptions.inheritedTitle') }}</div>
                  <div v-for="coupon in level.inherited_coupons" :key="'inh' + level.id + '-' + coupon.id">
                    {{ coupon.name }} <span class="sub-tooltip-from">({{ coupon.from_name }})</span>
                  </div>
                </div>
                <span class="sub-badge sub-badge--inherited">{{ $t('marketingSubscriptions.inheritedCount', { count: level.inherited_coupons.length }) }}</span>
              </el-tooltip>
              <span v-for="coupon in level.coupons" :key="'own' + level.id + '-' + coupon.id" class="sub-badge sub-badge--own">{{ coupon.name }}</span>
              <span v-if="!level.coupons.length && !level.inherited_coupons.length" class="text-muted">{{ $t('marketingSubscriptions.noneAssigned') }}</span>
            </div>
            <div>
              <a class="sub-count" @click="openSubscribers(family, level)">
                <i class="fa fa-users"></i> {{ level.active_subscribers }}
              </a>
            </div>
            <div>
              <p-switch v-model="level.active" type="success" @input="handleActive(level)"></p-switch>
            </div>
            <div class="sub-row__actions">
              <a class="sub-icon" :title="$t('general.edit')" @click="editLevel(level)"><i class="fa fa-pencil"></i></a>
              <a v-if="level.can_delete" class="sub-icon sub-icon--danger" :title="$t('general.delete')" @click="handleDelete(family, level)"><i class="fa fa-trash-o"></i></a>
              <a v-else class="sub-icon sub-icon--locked" :title="deleteBlockedReason(family, level)" @click="showDeleteBlocked(family, level)"><i class="fa fa-lock"></i></a>
            </div>
          </div>

          <a class="sub-family__add" @click="addLevel(family)">
            <i class="fa fa-plus"></i> {{ $t('marketingSubscriptions.addLevelOnTop', { name: topLevel(family).name }) }}
          </a>
        </div>
      </div>
    </div>

    <!-- Family edit: name + payment processor / account -->
    <vmodal name="subscriptionFamilyModal"
            class="subscriptionFamilyModal"
            width="95%"
            :maxWidth="520"
            :minWidth="300"
            height="auto"
            :adaptive="true"
            :clickToClose="false"
            @opened="onModalOpened">
      <div class="sub-modal__header">
        <h4>{{ $t('marketingSubscriptions.editFamily') }}</h4>
      </div>
      <div class="sub-modal__body">
        <div class="form-group">
          <label>{{ $t('marketingSubscriptions.familyName') }}*</label>
          <input type="text" class="form-control" maxlength="255" v-model="familyForm.name">
        </div>
        <!-- One select: the company's payment groups of the supported processors (euPlatesc only for now) -->
        <div class="form-group">
          <label>{{ $t('marketingSubscriptions.paymentProcessor') }}*</label>
          <el-select v-model="familyForm.payment_group_key" class="w-100" :disabled="familyForm.locked" :no-data-text="$t('marketingSubscriptions.noPaymentAccounts')">
            <el-option v-for="group in paymentGroups" :key="group.group_key" :label="paymentGroupLabel(group)" :value="group.group_key"></el-option>
          </el-select>
          <small v-if="familyForm.locked" class="text-muted">{{ $t('marketingSubscriptions.paymentLockedHint') }}</small>
          <small v-else-if="!paymentGroups.length" class="text-danger">{{ $t('marketingSubscriptions.noPaymentAccounts') }}</small>
        </div>
      </div>
      <div class="sub-modal__footer">
        <button class="btn btn-default" @click="$modal.hide('subscriptionFamilyModal')">{{ $t('general.cancel') }}</button>
        <button class="btn btn-purple-light btn-fill" :disabled="savingFamily" @click="saveFamily">{{ $t('general.save') }}</button>
      </div>
    </vmodal>
  </div>
</template>
<script>
  import Vue from 'vue'
  import {Input, Select, Option, Tooltip} from 'element-ui'
  import vmodal from 'vue-js-modal'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import swal from 'sweetalert2'
  import 'sweetalert2/dist/sweetalert2.css'
  Vue.use(Input)
  Vue.use(Select)
  Vue.use(Option)
  Vue.use(Tooltip)
  Vue.use(vmodal, { componentName: 'vmodal' })

  // Must match SubscriptionFamily::GATEWAYS in the API (/subscription/payment-groups
  // only returns groups of those processors).
  const GATEWAY_LABELS = {
    euplatesc: 'euPlatesc'
  }

  export default {
    components: {
      PSwitch
    },
    data () {
      return {
        loading: true,
        searchQuery: '',
        families: [],
        currency: '',
        paymentGroups: [],
        savingFamily: false,
        familyForm: {
          id: null,
          name: '',
          payment_group_key: '',
          locked: false
        }
      }
    },
    created () {
      this.currency = this.getCurrencySymbol(this.$session.get('selectedCompanyData').currency_iso)
      this.getFamilies()
      this.getPaymentGroups()
      this.$bus.$on('refresh', this.getFamilies)
    },
    beforeDestroy () {
      this.$bus.$off('refresh', this.getFamilies)
    },
    methods: {
      requestData () {
        return {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
      },
      getFamilies () {
        const data = this.requestData()
        if (this.searchQuery.length >= 3) {
          data.search = this.searchQuery
        }
        this.loading = true
        this.apiRequest('/subscription', 'GET', data, (response) => {
          if (response.status === true) {
            this.families = response.data || []
          }
          this.$nextTick(() => {
            this.loading = false
          })
        })
      },
      getPaymentGroups () {
        this.apiRequest('/subscription/payment-groups', 'GET', this.requestData(), (response) => {
          if (response.status === true) {
            this.paymentGroups = response.data || []
          }
        })
      },
      paymentGroupLabel (group) {
        return (group.group_name || group.group_key) + ' · ' + (GATEWAY_LABELS[group.gateway] || group.gateway)
      },
      onInput () {
        if (this.searchQuery.length >= 3 || this.searchQuery.length === 0) {
          this.getFamilies()
        }
      },
      topLevel (family) {
        return family.levels[family.levels.length - 1] || {}
      },
      editLevel (level) {
        this.$router.push({ path: '/marketing/subscriptions/edit/' + level.id }).catch(() => {})
      },
      addLevel (family) {
        this.$router.push({ path: '/marketing/subscriptions/add', query: { family_id: family.id } }).catch(() => {})
      },
      openSubscribers (family, level) {
        const query = { family_id: family.id }
        if (level) {
          query.subscription_id = level.id
        }
        this.$router.push({ path: '/marketing/subscriptions/subscribers', query }).catch(() => {})
      },
      handleActive (level) {
        const data = {
          ...this.requestData(),
          active: level.active ? 1 : 0
        }
        this.apiRequest('/subscription/' + level.id + '/status', 'POST', data, (response) => {
          if (response.status) {
            swal(this.$t('general.success') + '!', '', 'success').catch(() => {})
          } else {
            level.active = !level.active
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        })
      },
      deleteBlockedReason (family, level) {
        return level.id !== this.topLevel(family).id
          ? this.$t('marketingSubscriptions.notTopDelete')
          : this.$t('marketingSubscriptions.lockedDelete')
      },
      showDeleteBlocked (family, level) {
        swal('', this.deleteBlockedReason(family, level), 'info').catch(() => {})
      },
      handleDelete (family, level) {
        let text = this.$t('marketingSubscriptions.areYouSureDelete') + ' ' + level.name + '?'
        if (family.levels.length === 1) {
          text += ' ' + this.$t('marketingSubscriptions.deleteLastLevelWarning')
        }
        swal({
          text: text,
          type: 'question',
          showCancelButton: true
        }).then((result) => {
          if (!result) {
            return
          }
          this.apiRequest('/subscription/' + level.id, 'DELETE', this.requestData(), (response) => {
            if (response.status === true) {
              swal('', this.$t('marketingSubscriptions.deleted'), 'success').catch(() => {})
              this.getFamilies()
            } else {
              swal('', this.firstError(response), 'error').catch(() => {})
            }
          })
        }).catch(() => {})
      },
      /* ---------------- Family modal ---------------- */
      openFamilyModal (family) {
        this.familyForm = {
          id: family.id,
          name: family.name,
          payment_group_key: family.payment_group_key,
          locked: family.active_subscribers > 0
        }
        this.$modal.show('subscriptionFamilyModal')
      },
      onModalOpened () {
        // `.main-panel` has a transform, which makes it the containing block for
        // this modal's `position: fixed` container. Re-parent it to <body>.
        const el = document.querySelector('.subscriptionFamilyModal.vm--container')
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el)
        }
      },
      saveFamily () {
        if (!(this.familyForm.name || '').trim()) {
          swal('', this.$t('marketingSubscriptions.familyNameRequired'), 'warning').catch(() => {})
          return
        }
        const group = this.paymentGroups.find(item => item.group_key === this.familyForm.payment_group_key)
        if (!group) {
          swal('', this.$t('marketingSubscriptions.paymentRequired'), 'warning').catch(() => {})
          return
        }
        const data = {
          ...this.requestData(),
          name: this.familyForm.name,
          payment_gateway: group.gateway,
          payment_group_key: group.group_key
        }
        this.savingFamily = true
        this.apiRequest('/subscription/families/' + this.familyForm.id, 'PUT', data, (response) => {
          this.savingFamily = false
          if (response.status === true) {
            this.$modal.hide('subscriptionFamilyModal')
            swal('', this.$t('marketingSubscriptions.familySaved'), 'success').catch(() => {})
            this.getFamilies()
          } else {
            swal('', this.firstError(response), 'error').catch(() => {})
          }
        })
      },
      firstError (response) {
        const errors = (response && response.errors) || {}
        const key = Object.keys(errors)[0]
        if (!key) {
          return this.$t('validations.wentWrong')
        }
        return Array.isArray(errors[key]) ? errors[key][0] : errors[key]
      }
    }
  }
</script>
<style>
  .sub-families {
    min-height: 120px;
  }
  .sub-empty {
    padding: 30px 0;
  }
  .sub-family {
    padding: 14px 18px 10px;
    margin-bottom: 18px;
  }
  .sub-family__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
  .sub-family__title {
    font-size: 15px;
  }
  .sub-family__title .fa {
    color: #7e57c2;
    margin-right: 6px;
  }
  .sub-family__meta {
    color: #888;
    font-size: 13px;
    margin-left: 4px;
  }
  .sub-family__actions .btn {
    margin-left: 6px;
  }
  .sub-grid {
    display: grid;
    grid-template-columns: 40px minmax(160px, 1.6fr) 120px minmax(180px, 2fr) 90px 80px 70px;
    align-items: center;
    gap: 10px;
  }
  .sub-grid--head {
    padding: 8px 0 4px;
    font-size: 12px;
    color: #999;
    text-transform: none;
  }
  .sub-row {
    padding: 10px 0;
    border-bottom: 1px solid #f3f3f3;
    font-size: 14px;
  }
  .sub-row.is-inactive .sub-name__link {
    color: #999;
  }
  .sub-level {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #ede7f6;
    color: #5e35b1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
  }
  .sub-name {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .sub-name__link {
    cursor: pointer;
    font-weight: 600;
    color: inherit;
  }
  .sub-thumb {
    width: 36px;
    height: 36px;
    object-fit: cover;
    border-radius: 6px;
  }
  .sub-badge {
    display: inline-block;
    margin: 2px 4px 2px 0;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.4;
  }
  .sub-badge--own {
    background: #ede7f6;
    color: #5e35b1;
    border: 1px solid #d1c4e9;
  }
  .sub-badge--inherited {
    background: #fafafa;
    color: #777;
    border: 1px dashed #bbb;
    cursor: default;
  }
  .sub-badge--muted {
    background: #f5f5f5;
    color: #999;
    border: 1px solid #e0e0e0;
    font-size: 11px;
  }
  .sub-tooltip-title {
    font-weight: 600;
    margin-bottom: 4px;
  }
  .sub-tooltip-from {
    opacity: 0.7;
  }
  .sub-count {
    cursor: pointer;
    font-weight: 600;
  }
  .sub-row__actions {
    display: flex;
    gap: 12px;
    font-size: 16px;
  }
  .sub-icon {
    cursor: pointer;
    color: #666;
  }
  .sub-icon--danger {
    color: #e53935;
  }
  .sub-icon--locked {
    color: #bbb;
  }
  .sub-family__add {
    display: inline-block;
    margin-top: 10px;
    cursor: pointer;
    font-size: 13px;
  }
  .subscriptionFamilyModal .vm--modal {
    top: 60px !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 auto !important;
  }
  .sub-modal__header {
    padding: 15px;
    border-bottom: 1px solid #ccc;
  }
  .sub-modal__header h4 {
    margin: 0;
  }
  .sub-modal__body {
    padding: 15px;
  }
  .sub-modal__footer {
    padding: 15px;
    border-top: 1px solid #ccc;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
  .sub-modal__body .w-100 {
    width: 100%;
  }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/Subscriptions/Subscriptions.vue